// frontend/src/hooks/useChartPreferences.js
// Persists chart control state (metric, granularity, chartType) per chart per user.
// Follows the same pattern as useGridState — reads from ui_preferences, writes via updatePreference.
//
// Pass `chartId` as null/undefined/'' to skip persistence and keep state local to the component
// (transient — survives re-renders, lost on remount). The hook is always safe to call
// unconditionally regardless of whether the chart is persisted, satisfying React's Rules of Hooks.

import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const ZOOM_DEBOUNCE_MS = 800;

export const useChartPreferences = (chartId, defaults) => {
  const { userContext, updatePreference } = useAuth();
  const zoomTimerRef = useRef(null);
  const enabled = Boolean(chartId);

  // Local state used when chartId is falsy. React still calls useState on every render,
  // so this hook's hook-call sequence stays stable regardless of `enabled`.
  const [local, setLocal] = useState({
    metric: defaults.metric,
    granularity: defaults.granularity,
    chartType: defaults.chartType,
    navigatorZoom: null,
  });

  const persisted = useMemo(
    () => (enabled ? userContext?.user?.ui_preferences?.chart_preferences?.[chartId] || {} : {}),
    [enabled, userContext?.user?.ui_preferences?.chart_preferences, chartId],
  );

  const metric = enabled ? (persisted.metric || defaults.metric) : local.metric;
  const granularity = enabled ? (persisted.granularity || defaults.granularity) : local.granularity;
  const chartType = enabled ? (persisted.chartType || defaults.chartType) : local.chartType;
  const navigatorZoom = enabled ? (persisted.navigatorZoom || null) : local.navigatorZoom;

  // Functional updater — `prev` is read from AuthContext's latest-state
  // ref, so sequential calls in one tick merge safely.
  const update = useCallback((field, value) => {
    if (enabled) {
      updatePreference('chart_preferences', (prev) => ({
        ...(prev || {}),
        [chartId]: { ...((prev || {})[chartId] || {}), [field]: value },
      }));
    } else {
      setLocal((prev) => ({ ...prev, [field]: value }));
    }
  }, [enabled, chartId, updatePreference]);

  const setMetric = useCallback((v) => update('metric', v), [update]);
  const setGranularity = useCallback((v) => update('granularity', v), [update]);
  const setChartType = useCallback((v) => update('chartType', v), [update]);
  const setNavigatorZoom = useCallback((v) => {
    clearTimeout(zoomTimerRef.current);
    zoomTimerRef.current = setTimeout(() => update('navigatorZoom', v), ZOOM_DEBOUNCE_MS);
  }, [update]);

  return { metric, setMetric, granularity, setGranularity, chartType, setChartType, navigatorZoom, setNavigatorZoom };
};
